import {
  BIDDER_SETTLEMENT_STATUS_MAP,
  BidderPaymentStateSchema,
  InvoiceCheckoutModeSchema,
  InvoicePaymentStatusSchema,
  PaymentMethodSchema,
  refundContactLabel,
  refundOnItsWayLabel,
} from '@neon-one/auctions-types';
import type { BidderInvoiceTotals } from '@neon-one/auctions-types';
import type { MyBidsInvoiceSnapshot } from '../components/bids/types';
import { formatCents } from './formatCents';
import { paymentLineParts } from './paymentLine';
import { methodTypeLabel } from './paymentMethodLabel';
import { REFUND_RETRY_GRACE_MS } from '../components/bids/chargeStatus';

const Status = InvoicePaymentStatusSchema.enum;
const Method = PaymentMethodSchema.enum;

export const BannerMode = {
  Checkout: 'awaiting-checkout', AutoCharge: 'auto-charge', Processing: 'processing',
  Clearing: 'clearing', Paid: 'paid', Failed: 'failed', Refunded: 'refunded',
  PartialRefund: 'partially-refunded', RefundPending: 'refund-pending', Void: 'void',
} as const;
export type BannerMode = typeof BannerMode[keyof typeof BannerMode];
export const BannerEvent = { Receipt: 'viewReceipt', Update: 'updateCard', Checkout: 'checkout' } as const;
export const BannerSeverity = { Success: 'success', Info: 'info', Warn: 'warn' } as const;

export function invoiceMoney(invoice: MyBidsInvoiceSnapshot | null): BidderInvoiceTotals {
  if (invoice?.paymentStatus === Status.void) {
    return { dueCents: 0, paidCents: 0, clearingCents: 0, refundedCents: 0, refundSettlingCents: 0, owedBackCents: 0 };
  }
  const refundedCents = invoice?.refundedCents ?? 0;
  const refundSettlingCents = invoice?.pendingRefundCents ?? 0;
  const owedBackCents = invoice?.owedBackCents ?? 0;
  const net = Math.max(0, (invoice?.totalCents ?? 0) - refundedCents - refundSettlingCents - owedBackCents);
  const paid = invoice?.paymentStatus === Status.paid || invoice?.paymentStatus === Status.partially_refunded
    || (invoice?.paymentStatus === Status.processing && invoice.paymentState === BidderPaymentStateSchema.enum.clearing);
  const due = invoice?.paymentStatus === Status.pending || invoice?.paymentStatus === Status.failed
    || (invoice?.paymentStatus === Status.processing && !paid);
  return { dueCents: due ? net : 0, paidCents: paid ? net : 0,
    clearingCents: paid && invoice?.paymentState === BidderPaymentStateSchema.enum.clearing ? net : 0,
    refundedCents: refundedCents + refundSettlingCents, refundSettlingCents, owedBackCents };
}

export function bannerMode(invoice: MyBidsInvoiceSnapshot | null, money: BidderInvoiceTotals,
  awaitingCheckout: boolean, hasPaymentMethod: boolean): BannerMode {
  if (money.dueCents > 0) {
    if (invoice?.paymentStatus === Status.failed) return BannerMode.Failed;
    if (invoice?.paymentStatus === Status.processing) return BannerMode.Processing;
    if (invoice?.checkoutMode === InvoiceCheckoutModeSchema.enum.auto_charge && hasPaymentMethod) return BannerMode.AutoCharge;
    return awaitingCheckout || invoice?.checkoutMode === InvoiceCheckoutModeSchema.enum.offer_default
      ? BannerMode.Checkout : BannerMode.AutoCharge;
  }
  if (money.refundSettlingCents > 0 || money.owedBackCents > 0) return BannerMode.RefundPending;
  if (money.clearingCents > 0) return BannerMode.Clearing;
  if (money.refundedCents > 0) return money.paidCents > 0 ? BannerMode.PartialRefund : BannerMode.Refunded;
  if (money.paidCents > 0) return BannerMode.Paid;
  if (invoice?.paymentStatus === Status.refunded) return BannerMode.Refunded;
  if (invoice?.paymentStatus === Status.partially_refunded) return BannerMode.PartialRefund;
  return BannerMode.Void;
}

function declinedMethod(invoice: MyBidsInvoiceSnapshot | null): string {
  switch (invoice?.paymentMethod) {
    case Method.ach: return 'Your bank account';
    case Method.apple_pay: case Method.google_pay: case Method.paypal: case Method.venmo:
      return methodTypeLabel(invoice.paymentMethod);
    case Method.card: return 'Your card';
    default: return 'Your payment method';
  }
}

function refundHeadline(invoice: MyBidsInvoiceSnapshot | null, money: BidderInvoiceTotals): string {
  const failedAt = invoice?.oldestRefundFailedAt;
  if (money.owedBackCents > 0 && failedAt && Date.now() - Date.parse(failedAt) > REFUND_RETRY_GRACE_MS) {
    return refundContactLabel(invoice?.organizationName ?? 'the organization');
  }
  return refundOnItsWayLabel(formatCents(money.refundSettlingCents + money.owedBackCents));
}

export function bannerHeadline(mode: BannerMode, invoice: MyBidsInvoiceSnapshot | null,
  money: BidderInvoiceTotals, itemsLabel: string, savedMethodLabel: string | null = null): string {
  switch (mode) {
    case BannerMode.Checkout: return `Check out ${formatCents(money.dueCents)} for ${itemsLabel}`;
    case BannerMode.AutoCharge: return `We'll charge ${savedMethodLabel ?? 'your saved payment method'}`;
    case BannerMode.Processing: return `Charging ${invoice?.paymentMethod === Method.ach ? 'your bank account' : 'your payment method'}`;
    case BannerMode.Failed: return `${declinedMethod(invoice)} was declined`;
    case BannerMode.Clearing: return `${BIDDER_SETTLEMENT_STATUS_MAP.clearing.label} ${formatCents(money.paidCents)} · clearing`;
    case BannerMode.Paid: return `${BIDDER_SETTLEMENT_STATUS_MAP.paid_online.label} ${formatCents(money.paidCents)}`;
    case BannerMode.PartialRefund: return `${formatCents(money.refundedCents)} refunded · ${formatCents(money.paidCents)} paid`;
    case BannerMode.Refunded: return money.refundedCents > 0 ? `Refunded ${formatCents(money.refundedCents)}` : BIDDER_SETTLEMENT_STATUS_MAP.refunded.label;
    case BannerMode.RefundPending: return refundHeadline(invoice, money);
    case BannerMode.Void: return BIDDER_SETTLEMENT_STATUS_MAP.void.label;
  }
}

export function bannerSubline(mode: BannerMode, invoice: MyBidsInvoiceSnapshot | null,
  money: BidderInvoiceTotals, itemsLabel: string): string {
  if (mode === BannerMode.Void) return '';
  if (money.dueCents > 0) {
    const due = `${formatCents(money.dueCents)} across ${itemsLabel}`;
    return money.paidCents > 0 ? `${due} · ${formatCents(money.paidCents)} paid` : due;
  }
  if (mode === BannerMode.Refunded && money.refundedCents === 0) return 'Your payment was refunded';
  if (mode === BannerMode.PartialRefund) return itemsLabel;
  if (!invoice?.paymentMethod) return itemsLabel;
  return `${itemsLabel} · ${paidByPhrase(invoice.paymentMethod, invoice.paymentReference)}`;
}

/** How the bidder paid: "Paid by card", "Paid from your bank account", "Paid in Cash" (GT1-353). */
function paidByPhrase(method: NonNullable<MyBidsInvoiceSnapshot['paymentMethod']>, reference: string | null): string {
  switch (method) {
    case Method.cash: case Method.check: case Method.wire: {
      const { verb, tail } = paymentLineParts(method, reference, '');
      return tail ? `${verb} ${tail}` : verb;
    }
    case Method.ach: return 'Paid from your bank account';
    case Method.card: case Method.terminal: return 'Paid by card';
    default: return `Paid with ${methodTypeLabel(method)}`;
  }
}

export function bannerSeverity(mode: BannerMode): typeof BannerSeverity[keyof typeof BannerSeverity] {
  if (mode === BannerMode.Paid || mode === BannerMode.Clearing) return BannerSeverity.Success;
  if (mode === BannerMode.Failed || mode === BannerMode.Checkout) return BannerSeverity.Warn;
  return BannerSeverity.Info;
}

export function bannerIcon(mode: BannerMode): string | undefined {
  if (mode === BannerMode.Paid || mode === BannerMode.Clearing) return 'pi pi-check-circle';
  if (mode === BannerMode.Refunded || mode === BannerMode.PartialRefund || mode === BannerMode.RefundPending) return 'pi pi-replay';
  if (mode === BannerMode.Checkout) return 'pi pi-shopping-cart';
  if (mode === BannerMode.Failed) return 'pi pi-exclamation-circle';
  return undefined;
}
